import './bootstrap';
import {createApp, h} from 'vue';
import {createInertiaApp} from '@inertiajs/vue3';
import {resolvePageComponent} from 'laravel-vite-plugin/inertia-helpers';
import {router} from '@inertiajs/vue3';
import ConfiguratorLayout from './Layouts/ConfiguratorLayout.vue';
import AdminLayout from "../js/Layouts/AdminLayout.vue";
import PublicLayout from "../js/Layouts/PublicLayout.vue";
import {createPinia} from 'pinia'
import PrimeVue from 'primevue/config';
import './../../resources/css/theme.css';
import './Pages/Admin/Common/AdminTypography.scss';
import './../../resources/css/icons.scss';
import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css'
import Ripple from "primevue/ripple";
import i18n from "./Translations/i18n";
import {definePreset} from "@primevue/themes";
import Material from "@primevue/themes/material";
import {lightenColor} from "@/Utils/CssFunctions.js";
import 'material-icons/iconfont/filled.css';
import ToastService from 'primevue/toastservice';
import DialogService from 'primevue/dialogservice';
import ConfirmationService from 'primevue/confirmationservice';
import Tooltip from 'primevue/tooltip';

const appName = import.meta.env.VITE_APP_NAME || 'Product Configurator';
const pinia = createPinia()

const darkenColor = (hex, percentage) => {
    const factor = Math.max(0, 1 - (percentage / 100));
    const r = Math.max(0, Math.min(255, Math.round(parseInt(hex.slice(1, 3), 16) * factor)));
    const g = Math.max(0, Math.min(255, Math.round(parseInt(hex.slice(3, 5), 16) * factor)));
    const b = Math.max(0, Math.min(255, Math.round(parseInt(hex.slice(5, 7), 16) * factor)));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const resolveBugsnagVue = async () => {
    if (!window._bugsnag) {
        return null;
    }

    const [{default: Bugsnag}, {default: BugsnagPluginVue}] = await Promise.all([
        import('@bugsnag/js'),
        import('@bugsnag/plugin-vue'),
    ]);

    window.bugsnagClient = Bugsnag.start({
        apiKey: window._bugsnag,
        plugins: [new BugsnagPluginVue()],
        releaseStage: window._bugsnagReleaseStage,
        notifyReleaseStages: ['production', 'staging'],
        appVersion: window.version || undefined,
        onError: (event) => {
            const message = event.errors?.[0]?.errorMessage || '';

            if (
                message === 'ResizeObserver loop completed with undelivered notifications.' ||
                message === 'ResizeObserver loop limit exceeded'
            ) {
                return false;
            }

            return true;
        },
    });

    return Bugsnag.getPlugin('vue');
};



const preset = definePreset(Material, {
    components: {
        button: {
            colorScheme: {
                light: {
                    root: {
                        primary: {
                            hoverBackground: darkenColor(window.primaryColor, 12),
                            hoverBorderColor: darkenColor(window.primaryColor, 12),
                            activeBackground: darkenColor(window.primaryColor, 18),
                            activeBorderColor: darkenColor(window.primaryColor, 18),
                        }
                    }
                },
                text: {
                    primary: {
                        hover: {
                            background: lightenColor(window.primaryColor, 8),
                        }
                    }
                },
                dark: {
                    primary: {
                        color: '#ffffff',
                        hoverColor: '#ffffff',
                        activeColor: '#ffffff'
                    },
                    info: {
                        color: '#ffffff',
                        hoverColor: '#ffffff',
                        activeColor: '#ffffff'
                    }
                }
            }
        }
    },
    semantic: {
        colorScheme: {
            light: {
                primary: {
                    color: window.primaryColor,
                    50: lightenColor(window.primaryColor, 50),
                    100: lightenColor(window.primaryColor, 45),
                    200: lightenColor(window.primaryColor, 40),
                    300: lightenColor(window.primaryColor, 35),
                    400: lightenColor(window.primaryColor, 30),
                    500: window.primaryColor,
                    600: darkenColor(window.primaryColor, 8),
                    700: darkenColor(window.primaryColor, 14),
                    800: darkenColor(window.primaryColor, 20),
                },
                secondary: {
                    color: window.secondaryColor,
                    50: lightenColor(window.secondaryColor, 50),
                    100: lightenColor(window.secondaryColor, 45),
                    200: lightenColor(window.secondaryColor, 40),
                    300: lightenColor(window.secondaryColor, 35),
                    400: lightenColor(window.secondaryColor, 30),
                },
                surface: {
                    color: window.surfaceColor,
                    50: lightenColor(window.surfaceColor, 50),
                    100: lightenColor(window.surfaceColor, 45),
                    200: lightenColor(window.surfaceColor, 40),
                    300: lightenColor(window.surfaceColor, 35),
                    400: lightenColor(window.surfaceColor, 30),
                },
            },

            dark: {
                primary: {
                    color: window.primaryColor
                },
                secondary: {
                    color: window.secondaryColor
                }
            }
        }
    }
});

void (async () => {
    const bugsnagVue = await resolveBugsnagVue();

    await createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => {

        // if the name starts with Auth/ change it to Admin/Auth
        if (name.startsWith('Auth/')) {
            name = name.replace('Auth/', 'Admin/Auth/');
        }

        const page = resolvePageComponent(
            `./Pages/${name}.vue`,
            import.meta.glob("./Pages/**/*.vue")
        );

        page.then(({default: component}) => {
            const layoutMapping = {
                admin: AdminLayout,
                public: PublicLayout
            };

            const lowerCaseName = name.toLowerCase();
            const layoutKey = Object.keys(layoutMapping).find(key => lowerCaseName.includes(key));

            component.layout = layoutMapping[layoutKey] || ConfiguratorLayout;
        });

        return page;
    },
    setup({el, App, props, plugin}) {
        // Update CSRF token header before each Inertia request
        router.on('before', (event) => {
            const token = document.querySelector('meta[name="csrf-token"]')?.content;
            if (token) {
                window.axios.defaults.headers.common['X-CSRF-TOKEN'] = token;
            }
        });

        // Update i18n locale when user language changes
        router.on('navigate', (event) => {
            const userLocale = event.detail.page?.props?.auth?.user?.language;
            if (userLocale && i18n.global.locale.value !== userLocale) {
                i18n.global.locale.value = userLocale;
            }
        });

        const app = createApp({render: () => h(App, props)});

        if (bugsnagVue) {
            app.use(bugsnagVue);
        }

        return app
            .use(plugin)
            .use(ToastService)
            .use(i18n)
            .use(PrimeVue, {
                ripple: true,
                theme: {
                    preset: preset,
                    options: {
                        darkModeSelector: '.configurator-dark',
                    },
                },
                unstyled: false
            })
            .use(pinia)
            .use(DialogService)
                        .use(ConfirmationService)
            .directive('ripple', Ripple)
            .directive('tooltip', Tooltip)
            .mount(el);
    },
    progress: {
        color: '#4B5563',
    },
    });
})();
